import React, { useEffect, useState } from 'react';
import { mergeStyleSets, mergeStyles } from '@fluentui/react';
import ErrorMessageComponent from '../error-message/error-message.component';

import useAuthStatus from '../../hooks/auth/AuthHook';
import ApiService from '../../services/api/api.service';

import ItemActividadResumen from '../../models/ItemActividadResumen';
import ItemActividadResumenItem from '../../models/ItemActividadResumenItem';
import ElementEnum from '../../enums/ElementEnum';
import ItemPermiso from '../../models/ItemPermiso';
import NoPermissionComponent from '../no-permission/no-permission.component';

import iconMultiple from '../../assets/img24/multiple_24.png';
import iconAbrir from '../../assets/img24/abrir_24.png';
import iconPlay from '../../assets/img24/play_24.png';
import iconPause from '../../assets/img24/pause_24.png';
import iconCerrado from '../../assets/img24/cerrado_24.png';
import iconFinalizado from '../../assets/img24/finalizado_24.png';
import iconReloj from '../../assets/img24/reloj_24.png';

const getCardIcon = (descripcion: string): string => {
    const d = descripcion.toLowerCase();
    if (d.includes('total')) return iconMultiple;
    if (d.includes('abiert')) return iconAbrir;
    if (d.includes('inici') || d.includes('curso')) return iconPlay;
    if (d.includes('pendiente')) return iconPause;
    if (d.includes('cerrad')) return iconCerrado;
    if (d.includes('finaliz')) return iconFinalizado;
    return iconReloj;
};

const stackClasses = mergeStyleSets({
    sectionTitle: {
        display: 'block',
        fontSize: 13,
        fontWeight: 600,
        color: '#555',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 10,
        marginTop: 8,
    },
    card: {
        borderRadius: 14,
        padding: '16px 12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 100,
        cursor: 'default',
        transition: 'box-shadow 0.2s',
        selectors: {
            ':hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            },
        },
    },
    cardIconRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
    },
    cardValue: {
        fontSize: 38,
        fontWeight: 700,
        lineHeight: '1',
        color: '#000',
    },
    cardIcon: {
        opacity: 0.6,
    },
    cardLabel: {
        fontSize: 11,
        fontWeight: 500,
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: '#444',
    },
});

interface IActivityResumeStackProps {
    updateTime: number;
    onLoading: () => void;
    onLoaded: () => void;
};

const ActivityResumeStackComponent = (props: IActivityResumeStackProps) => {
    const auth = useAuthStatus();
    const dashboardPermission : ItemPermiso = auth.getPermission(ElementEnum.DASHBOARD);
    
    const [activityResume, setActivityResume] = useState<ItemActividadResumen[]>([]);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const onLoading = props.onLoading;
    const onLoaded = props.onLoaded;

    useEffect(() => {
        let timer : NodeJS.Timeout;
        const getData = (showLoader: boolean) : void => {
            if (auth.user && dashboardPermission.Ver) {
                if (showLoader) {
                    setLoading(true);
                }
                setError('');
                ApiService.Post<any>("ResumenActividad", "GetDashBoard", auth.user).then((response: any) => {
                    timer = setTimeout(() => {
                        getData(false);
                    }, props.updateTime * 1000);
                    setActivityResume(ItemActividadResumen.parse(response));
                    setLoading(false);
                }, (error: string) => {
                    timer = setTimeout(() => {
                        getData(false);
                    }, props.updateTime * 1000);
                    setError(error);
                    setLoading(false);
                });
            }
            else {
                setLoading(false);
            }
        };

        getData(true);

        return () => {
            setActivityResume([]);
            setError('');
            setLoading(false);
            clearTimeout(timer);
        };
    }, [props.updateTime, dashboardPermission.Ver, auth.user]);

    useEffect(() => {
        if (!loading) {
            onLoaded();
        }
        else {
            onLoading();
        }
    }, [loading, onLoaded, onLoading]);

    return (
        dashboardPermission.Ver ? (
            <>
                {
                    activityResume.map((item: ItemActividadResumen, index: number) => {
                        const ordenPilas = ['Total', 'Abiertas', 'Iniciadas', 'Pendientes', 'Cerradas'];
                        const itemsFiltrados = item.items
                            .sort((a: ItemActividadResumenItem, b: ItemActividadResumenItem) => {
                                const posA = ordenPilas.findIndex(o => a.props.descripcion.toLowerCase().includes(o.toLowerCase()));
                                const posB = ordenPilas.findIndex(o => b.props.descripcion.toLowerCase().includes(o.toLowerCase()));
                                const rankA = posA === -1 ? 99 : posA;
                                const rankB = posB === -1 ? 99 : posB;
                                return rankA - rankB;
                            });

                        return (
                            <div key={index} className='row mx-0 my-2'>
                                <div className='col-12 p-0 mb-1'>
                                    <span className={stackClasses.sectionTitle}>{item.title}</span>
                                </div>
                                <div className='col-12 p-0'>
                                    <div className='row m-0'>
                                        {
                                            itemsFiltrados.map((subitem: ItemActividadResumenItem, index2: number) => {
                                                const bg = subitem.props.backgroundColor || '#ffffff';
                                                return (
                                                <div key={index2} className='col-6 col-sm-4 col-md-6 col-lg-3 p-2'>
                                                    <div className={mergeStyles(stackClasses.card, {
                                                        backgroundColor: bg,
                                                    })}>
                                                        <div className={stackClasses.cardIconRow}>
                                                            <img
                                                                className={stackClasses.cardIcon}
                                                                src={getCardIcon(subitem.props.descripcion)}
                                                                alt=""
                                                                height={22}
                                                                width={22} />
                                                            <span className={stackClasses.cardValue}>
                                                                {subitem.props.valor}
                                                            </span>
                                                        </div>
                                                        <span className={stackClasses.cardLabel}>
                                                            {subitem.props.descripcion}
                                                        </span>
                                                    </div>
                                                </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
                <ErrorMessageComponent message={error} />
            </>
        ) : (
            <NoPermissionComponent showButton={false} />
        )
    );
};

export default ActivityResumeStackComponent;
